import { useEffect, useState } from 'react'
import { type CaptureData } from '#app/types/capture.ts'

interface StaticResourcesError {
	message: string
	status: number
	statusText: string
	internal: boolean
	data: string
}

interface UseStaticCaptureResult {
	captureData: CaptureData | null
	loading: boolean
	error: StaticResourcesError | null
}

export function useStaticCapture(captureId: string): UseStaticCaptureResult {
	const [captureData, setCaptureData] = useState<CaptureData | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<StaticResourcesError | null>(null)

	useEffect(() => {
		const abortController = new AbortController()

		async function loadCaptureData() {
			try {
				setLoading(true)
				setError(null)

				const captureUrl = `${ENV.STATIC_ASSET_URL}/${captureId}/!index.json`

				const captureResponse = await fetch(captureUrl, {
					signal: abortController.signal,
				})

				if (abortController.signal.aborted) return

				if (!captureResponse.ok) {
					setError({
						message: `Failed to load capture data: ${captureUrl}`,
						status: captureResponse.status,
						statusText: captureResponse.statusText,
						internal: true,
						data: captureUrl,
					})
					return
				}

				const capture = await captureResponse.json()

				if (abortController.signal.aborted) return

				setCaptureData(capture as CaptureData)
			} catch (err) {
				if (abortController.signal.aborted) return
				setError({
					message:
						err instanceof Error ? err.message : 'Failed to load capture data',
					status: 500,
					statusText: 'Internal Server Error',
					internal: true,
					data: 'Failed to load capture data',
				})
				console.error('Failed to load capture data:', err)
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false)
				}
			}
		}

		void loadCaptureData()

		return () => {
			abortController.abort()
		}
	}, [captureId])

	return {
		captureData,
		loading,
		error,
	}
}
